import { useState } from 'react';

/** Module-level Set for bookmarked URLs — persists for the page session without Web Storage. */
const _bookmarkedUrls = new Set<string>();
import { Button } from '@bloomcode/ui';
import { useCopyToClipboard } from '@bloomcode/hooks';

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
}

export function ShareButtons({ url, title, description }: ShareButtonsProps) {
  const { copy, copied } = useCopyToClipboard();
  const [bookmarked, setBookmarked] = useState(() => _bookmarkedUrls.has(url));

  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;

  const handleCopyLink = async () => {
    await copy(fullUrl);
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(fullUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${description || title}\n\n${fullUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleBookmark = () => {
    if (bookmarked) {
      _bookmarkedUrls.delete(url);
      setBookmarked(false);
    } else {
      _bookmarkedUrls.add(url);
      setBookmarked(true);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground mr-2">Share:</span>

      <Button variant="outline" size="sm" onClick={handleTwitterShare} title="Share on Twitter">
        𝕏
      </Button>

      <Button variant="outline" size="sm" onClick={handleFacebookShare} title="Share on Facebook">
        f
      </Button>

      <Button variant="outline" size="sm" onClick={handleLinkedInShare} title="Share on LinkedIn">
        in
      </Button>

      <Button variant="outline" size="sm" onClick={handleEmailShare} title="Share via Email">
        ✉️
      </Button>

      <Button variant="outline" size="sm" onClick={handleCopyLink} title="Copy link">
        {copied ? '✓' : '🔗'}
      </Button>

      <div className="border-l pl-2 ml-2">
        <Button
          variant={bookmarked ? 'default' : 'outline'}
          size="sm"
          onClick={handleBookmark}
          title={bookmarked ? 'Remove bookmark' : 'Bookmark this'}
        >
          {bookmarked ? '★' : '☆'}
        </Button>
      </div>
    </div>
  );
}
